import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store";
import { actionGetLogsAuditoria } from "../../features/AuditoriaLogs/slices";

type TipoLog = "excecoes" | "auditoria";

const PageAuditoria = () => {
  const dispatch = useDispatch();

  const [dataFiltro, setDataFiltro] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [tipoLog, setTipoLog] = useState<TipoLog>("excecoes");
  const [tipoFiltro, setTipoFiltro] = useState("TODOS");
  const [buscaTexto, setBuscaTexto] = useState("");
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [logSelecionado, setLogSelecionado] = useState<any>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { logs, status, totalPaginas, totalItens } = useSelector(
    (state: RootState) => state.auditoria,
  );
  const loading = status === "loading";

  const carregarLogs = (pagina = paginaAtual) => {
    dispatch(
      actionGetLogsAuditoria({
        data: dataFiltro,
        tipo: tipoLog,
        busca: buscaTexto || undefined,
        pagina,
        itensPorPagina: 20,
      }),
    );
  };

  useEffect(() => {
    setPaginaAtual(1);
    setTipoFiltro("TODOS");
    setBuscaTexto("");
    setLogSelecionado(null);
    carregarLogs(1);
  }, [dataFiltro, tipoLog]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPaginaAtual(1);
      carregarLogs(1);
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [buscaTexto]);

  const logsFiltrados = (logs || []).filter((log: any) => {
    if (tipoLog === "excecoes") {
      return tipoFiltro === "TODOS" || log.tipoEvento === tipoFiltro;
    }
    return true;
  });

  const irParaPagina = (pagina: number) => {
    if (pagina < 1 || pagina > totalPaginas) return;
    setPaginaAtual(pagina);
    carregarLogs(pagina);
  };

  const obterEstiloBadge = (tipo: string) => {
    if (tipo?.includes("ERR") || tipo?.includes("EXCEPTION"))
      return { bg: "#fce8e6", texto: "#c5221f" };
    if (tipo === "API_INICIADA") return { bg: "#e6f4ea", texto: "#137333" };
    return { bg: "#e8f0fe", texto: "#1a73e8" };
  };

  const obterEstiloStatus = (status: number) => {
    if (status >= 500) return { bg: "#fce8e6", texto: "#c5221f" };
    if (status >= 400) return { bg: "#fef7e0", texto: "#b06000" };
    return { bg: "#e6f4ea", texto: "#137333" };
  };

  const estiloAba = (ativa: boolean) => ({
    padding: "8px 20px",
    border: "none",
    borderBottom: ativa ? "3px solid #2980b9" : "3px solid transparent",
    backgroundColor: "transparent",
    color: ativa ? "#2980b9" : "#7f8c8d",
    fontWeight: ativa ? "bold" : "normal",
    cursor: "pointer",
    fontSize: "14px",
    transition: "0.2s",
  });

  const estiloBotaoPagina = (ativo: boolean, desabilitado: boolean) => ({
    padding: "6px 12px",
    border: "1px solid #ccc",
    borderRadius: "4px",
    backgroundColor: ativo ? "#2980b9" : "#fff",
    color: ativo ? "#fff" : desabilitado ? "#ccc" : "#2c3e50",
    cursor: desabilitado ? "not-allowed" : "pointer",
    fontSize: "13px",
    fontWeight: ativo ? "bold" : "normal",
  });

  return (
    <div
      style={{
        padding: "24px",
        fontFamily: "Arial, sans-serif",
        backgroundColor: "#f8f9fa",
        minHeight: "100vh",
      }}
    >
      <div style={{ marginBottom: "8px" }}>
        <h1 style={{ margin: 0, fontSize: "24px", color: "#2c3e50" }}>
          Auditoria & Painel de Exceções
        </h1>
        <p style={{ margin: "4px 0 0 0", color: "#7f8c8d", fontSize: "14px" }}>
          Monitoramento de infraestrutura e erros em tempo real
        </p>
      </div>

      <div
        style={{
          display: "flex",
          borderBottom: "1px solid #e4e7eb",
          marginBottom: "20px",
        }}
      >
        <button
          style={estiloAba(tipoLog === "excecoes")}
          onClick={() => setTipoLog("excecoes")}
        >
          🚨 Exceções
        </button>
        <button
          style={estiloAba(tipoLog === "auditoria")}
          onClick={() => setTipoLog("auditoria")}
        >
          📋 Auditoria de Acesso
        </button>
      </div>

      <div
        style={{
          display: "flex",
          gap: "16px",
          backgroundColor: "#fff",
          padding: "16px",
          borderRadius: "8px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          marginBottom: "20px",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <label
            style={{ fontSize: "12px", fontWeight: "bold", color: "#555" }}
          >
            Data do Log
          </label>
          <input
            title={""}
            type="date"
            value={dataFiltro}
            onChange={(e) => setDataFiltro(e.target.value)}
            style={{
              padding: "8px",
              borderRadius: "4px",
              border: "1px solid #ccc",
              fontSize: "14px",
            }}
          />
        </div>

        {tipoLog === "excecoes" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <label
              style={{ fontSize: "12px", fontWeight: "bold", color: "#555" }}
            >
              Tipo de Evento
            </label>
            <select
              title={""}
              value={tipoFiltro}
              onChange={(e) => setTipoFiltro(e.target.value)}
              style={{
                padding: "8px",
                borderRadius: "4px",
                border: "1px solid #ccc",
                fontSize: "14px",
                minWidth: "180px",
              }}
            >
              <option value="TODOS">Todos os Eventos</option>
              <option value="EXCEPTION_DAL">EXCEPTION_DAL</option>
              <option value="EXCEPTION_DAL_VOID">EXCEPTION_DAL_VOID</option>
              <option value="CONTROLLER_PIXEL_ERR">CONTROLLER_PIXEL_ERR</option>
              <option value="API_INICIADA">API_INICIADA</option>
            </select>
          </div>
        )}

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            flex: 1,
            minWidth: "200px",
          }}
        >
          <label
            style={{ fontSize: "12px", fontWeight: "bold", color: "#555" }}
          >
            Buscar por texto livre
          </label>
          <input
            type="text"
            placeholder={
              tipoLog === "excecoes"
                ? "Buscar por mensagem, endpoint..."
                : "Buscar por endpoint, usuário, IP..."
            }
            value={buscaTexto}
            onChange={(e) => setBuscaTexto(e.target.value)}
            style={{
              padding: "8px",
              borderRadius: "4px",
              border: "1px solid #ccc",
              fontSize: "14px",
            }}
          />
        </div>

        <button
          onClick={() => carregarLogs(paginaAtual)}
          style={{
            padding: "10px 16px",
            alignSelf: "flex-end",
            backgroundColor: "#2980b9",
            color: "#fff",
            border: "none",
            borderRadius: "4px",
            fontWeight: "bold",
            cursor: "pointer",
          }}
        >
          🔄 Atualizar
        </button>
      </div>

      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: "8px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          overflow: "hidden",
        }}
      >
        {loading ? (
          <div
            style={{ padding: "40px", textAlign: "center", color: "#7f8c8d" }}
          >
            Carregando logs do servidor...
          </div>
        ) : logsFiltrados.length === 0 ? (
          <div
            style={{ padding: "40px", textAlign: "center", color: "#7f8c8d" }}
          >
            Nenhum log encontrado para os filtros aplicados.
          </div>
        ) : tipoLog === "excecoes" ? (
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              textAlign: "left",
              fontSize: "14px",
            }}
          >
            <thead>
              <tr
                style={{
                  backgroundColor: "#f1f2f6",
                  color: "#2c3e50",
                  borderBottom: "2px solid #e4e7eb",
                }}
              >
                <th style={{ padding: "12px 16px" }}>Horário</th>
                <th style={{ padding: "12px 16px" }}>Evento / Exceção</th>
                <th style={{ padding: "12px 16px" }}>Origem / Endpoint</th>
                <th style={{ padding: "12px 16px" }}>Mensagem de Erro</th>
                <th style={{ padding: "12px 16px", textAlign: "center" }}>
                  Ações
                </th>
              </tr>
            </thead>
            <tbody>
              {logsFiltrados.map((log: any, index: number) => {
                const tipo = log.tipoEvento || "DESCONHECIDO";
                const estiloBadge = obterEstiloBadge(tipo);
                const tsRaw =
                  typeof log.timestamp === "string"
                    ? log.timestamp
                    : String(log.timestamp ?? "");
                const horarioLocal = tsRaw
                  ? new Date(tsRaw.replace(" ", "T")).toLocaleTimeString(
                      "pt-BR",
                    )
                  : "N/A";
                const mensagemExibida =
                  log.mensagem || log.message || "Sem mensagem.";
                let origemExibida = log.endpoint || log.origem || "N/A";
                if (
                  origemExibida === "N/A" &&
                  log.stackTrace &&
                  typeof log.stackTrace === "string"
                ) {
                  const linhas = log.stackTrace.split("\r\n");
                  const ultima = linhas[linhas.length - 1] || linhas[0];
                  if (ultima)
                    origemExibida = ultima.replace("   at ", "").trim();
                } else if (origemExibida === "N/A" && tipo === "API_INICIADA") {
                  origemExibida = "System.Startup";
                }
                return (
                  <tr key={index} style={{ borderBottom: "1px solid #e4e7eb" }}>
                    <td
                      style={{
                        padding: "12px 16px",
                        color: "#555",
                        fontWeight: "bold",
                      }}
                    >
                      {horarioLocal}
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      <span
                        style={{
                          backgroundColor: estiloBadge.bg,
                          color: estiloBadge.texto,
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          fontWeight: "bold",
                        }}
                      >
                        {tipo}
                      </span>
                    </td>
                    <td
                      style={{
                        padding: "12px 16px",
                        maxWidth: "220px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={origemExibida}
                    >
                      <small style={{ color: "#7f8c8d" }}>
                        {origemExibida}
                      </small>
                    </td>
                    <td
                      style={{
                        padding: "12px 16px",
                        color: "#4a4a4a",
                        maxWidth: "350px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={mensagemExibida}
                    >
                      {mensagemExibida}
                    </td>
                    <td style={{ padding: "12px 16px", textAlign: "center" }}>
                      <button
                        onClick={() => setLogSelecionado(log)}
                        style={{
                          padding: "4px 10px",
                          backgroundColor: "#f1f2f6",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                          cursor: "pointer",
                        }}
                      >
                        🔍 Detalhes
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              textAlign: "left",
              fontSize: "14px",
            }}
          >
            <thead>
              <tr
                style={{
                  backgroundColor: "#f1f2f6",
                  color: "#2c3e50",
                  borderBottom: "2px solid #e4e7eb",
                }}
              >
                <th style={{ padding: "12px 16px" }}>Horário</th>
                <th style={{ padding: "12px 16px" }}>Usuário</th>
                <th style={{ padding: "12px 16px" }}>Endpoint</th>
                <th style={{ padding: "12px 16px", textAlign: "center" }}>
                  Status
                </th>
                <th style={{ padding: "12px 16px", textAlign: "right" }}>
                  Tempo
                </th>
                <th style={{ padding: "12px 16px", textAlign: "center" }}>
                  Servidor
                </th>
                <th style={{ padding: "12px 16px", textAlign: "center" }}>
                  Ações
                </th>
              </tr>
            </thead>
            <tbody>
              {logsFiltrados.map((log: any, index: number) => {
                const tsRaw =
                  typeof log.timestamp === "string"
                    ? log.timestamp
                    : String(log.timestamp ?? "");
                const horarioLocal = tsRaw
                  ? new Date(tsRaw.replace(" ", "T")).toLocaleTimeString(
                      "pt-BR",
                    )
                  : "N/A";
                const estiloStatus = obterEstiloStatus(log.statusCode);
                return (
                  <tr key={index} style={{ borderBottom: "1px solid #e4e7eb" }}>
                    <td
                      style={{
                        padding: "12px 16px",
                        color: "#555",
                        fontWeight: "bold",
                      }}
                    >
                      {horarioLocal}
                    </td>
                    <td style={{ padding: "12px 16px", color: "#2c3e50" }}>
                      <div style={{ fontSize: "13px" }}>
                        {log.usuario || "Anônimo"}
                      </div>
                      <small style={{ color: "#7f8c8d" }}>{log.ip}</small>
                    </td>
                    <td
                      style={{
                        padding: "12px 16px",
                        maxWidth: "300px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={log.endpoint}
                    >
                      <code style={{ fontSize: "12px", color: "#2c3e50" }}>
                        {log.endpoint}
                      </code>
                    </td>
                    <td style={{ padding: "12px 16px", textAlign: "center" }}>
                      <span
                        style={{
                          backgroundColor: estiloStatus.bg,
                          color: estiloStatus.texto,
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          fontWeight: "bold",
                        }}
                      >
                        {log.statusCode}
                      </span>
                    </td>
                    <td
                      style={{
                        padding: "12px 16px",
                        textAlign: "right",
                        color: "#555",
                        fontFamily: "monospace",
                      }}
                    >
                      {log.tempoExecucao}
                    </td>
                    <td
                      style={{
                        padding: "12px 16px",
                        textAlign: "center",
                        fontSize: "11px",
                        color: "#7f8c8d",
                        fontFamily: "monospace",
                      }}
                    >
                      {typeof log.ramUsadaMB === "number" && (
                        <div>🧠 {log.ramUsadaMB}MB</div>
                      )}
                      {typeof log.cpuPercent === "number" && (
                        <div>⚡ {log.cpuPercent.toFixed(1)}%</div>
                      )}
                      {typeof log.requisicoesSumulaneas === "number" && (
                        <div>📊 {log.requisicoesSumulaneas} req</div>
                      )}
                    </td>
                    <td style={{ padding: "12px 16px", textAlign: "center" }}>
                      <button
                        onClick={() => setLogSelecionado(log)}
                        style={{
                          padding: "4px 10px",
                          backgroundColor: "#f1f2f6",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                          cursor: "pointer",
                        }}
                      >
                        🔍 Detalhes
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {!loading && totalItens > 0 && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "16px",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          <span style={{ fontSize: "13px", color: "#7f8c8d" }}>
            {totalItens} registro{totalItens !== 1 ? "s" : ""} encontrado
            {totalItens !== 1 ? "s" : ""} — página {paginaAtual} de{" "}
            {totalPaginas}
          </span>
          <div style={{ display: "flex", gap: "6px" }}>
            <button
              onClick={() => irParaPagina(1)}
              disabled={paginaAtual === 1}
              style={estiloBotaoPagina(false, paginaAtual === 1)}
            >
              « Primeira
            </button>
            <button
              onClick={() => irParaPagina(paginaAtual - 1)}
              disabled={paginaAtual === 1}
              style={estiloBotaoPagina(false, paginaAtual === 1)}
            >
              ‹ Anterior
            </button>
            <span style={estiloBotaoPagina(true, false)}>{paginaAtual}</span>
            <button
              onClick={() => irParaPagina(paginaAtual + 1)}
              disabled={paginaAtual === totalPaginas}
              style={estiloBotaoPagina(false, paginaAtual === totalPaginas)}
            >
              Próxima ›
            </button>
            <button
              onClick={() => irParaPagina(totalPaginas)}
              disabled={paginaAtual === totalPaginas}
              style={estiloBotaoPagina(false, paginaAtual === totalPaginas)}
            >
              Última »
            </button>
          </div>
        </div>
      )}

      {logSelecionado && (
        <>
          <div
            style={{
              position: "fixed",
              top: 0,
              right: 0,
              width: "450px",
              height: "100vh",
              backgroundColor: "#fff",
              boxShadow: "-4px 0 15px rgba(0,0,0,0.15)",
              padding: "24px",
              overflowY: "auto",
              zIndex: 1000,
              display: "flex",
              flexDirection: "column",
              gap: "16px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: "1px solid #eee",
                paddingBottom: "12px",
              }}
            >
              <h3 style={{ margin: 0, color: "#2c3e50" }}>
                Detalhes do Registro
              </h3>
              <button
                onClick={() => setLogSelecionado(null)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "20px",
                  cursor: "pointer",
                  color: "#999",
                }}
              >
                ✕
              </button>
            </div>

            <div>
              <label
                style={{
                  fontSize: "11px",
                  fontWeight: "bold",
                  color: "#999",
                  display: "block",
                  marginBottom: "2px",
                }}
              >
                TIMESTAMP COMPLETO
              </label>
              <span>{logSelecionado.timestamp}</span>
            </div>

            {logSelecionado.usuario && (
              <div>
                <label
                  style={{
                    fontSize: "11px",
                    fontWeight: "bold",
                    color: "#999",
                    display: "block",
                    marginBottom: "2px",
                  }}
                >
                  USUÁRIO
                </label>
                <span>
                  {logSelecionado.usuario}{" "}
                  <small style={{ color: "#999" }}>
                    ({logSelecionado.usuarioId})
                  </small>
                </span>
              </div>
            )}
            {logSelecionado.ip && (
              <div>
                <label
                  style={{
                    fontSize: "11px",
                    fontWeight: "bold",
                    color: "#999",
                    display: "block",
                    marginBottom: "2px",
                  }}
                >
                  IP / USER AGENT
                </label>
                <div style={{ fontSize: "13px" }}>{logSelecionado.ip}</div>
                <small style={{ color: "#7f8c8d", wordBreak: "break-all" }}>
                  {logSelecionado.userAgent}
                </small>
              </div>
            )}
            {logSelecionado.tempoExecucao && (
              <div>
                <label
                  style={{
                    fontSize: "11px",
                    fontWeight: "bold",
                    color: "#999",
                    display: "block",
                    marginBottom: "2px",
                  }}
                >
                  TEMPO DE EXECUÇÃO
                </label>
                <span
                  style={{
                    fontFamily: "monospace",
                    fontSize: "15px",
                    color: "#2980b9",
                  }}
                >
                  {logSelecionado.tempoExecucao}
                </span>
              </div>
            )}

            {(typeof logSelecionado.ramUsadaMB === "number" ||
              typeof logSelecionado.cpuPercent === "number" ||
              typeof logSelecionado.threadCount === "number" ||
              typeof logSelecionado.requisicoesSumulaneas === "number") && (
              <div>
                <label
                  style={{
                    fontSize: "11px",
                    fontWeight: "bold",
                    color: "#999",
                    display: "block",
                    marginBottom: "6px",
                  }}
                >
                  SAÚDE DO SERVIDOR NO MOMENTO
                </label>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "8px",
                  }}
                >
                  {typeof logSelecionado.ramUsadaMB === "number" && (
                    <div
                      style={{
                        backgroundColor: "#f1f2f6",
                        borderRadius: "6px",
                        padding: "8px 10px",
                      }}
                    >
                      <div style={{ fontSize: "10px", color: "#7f8c8d" }}>
                        RAM USADA
                      </div>
                      <div
                        style={{
                          fontSize: "15px",
                          fontWeight: "bold",
                          color: "#2c3e50",
                        }}
                      >
                        {logSelecionado.ramUsadaMB} MB
                      </div>
                    </div>
                  )}
                  {typeof logSelecionado.cpuPercent === "number" && (
                    <div
                      style={{
                        backgroundColor: "#f1f2f6",
                        borderRadius: "6px",
                        padding: "8px 10px",
                      }}
                    >
                      <div style={{ fontSize: "10px", color: "#7f8c8d" }}>
                        CPU
                      </div>
                      <div
                        style={{
                          fontSize: "15px",
                          fontWeight: "bold",
                          color: "#2c3e50",
                        }}
                      >
                        {logSelecionado.cpuPercent.toFixed(2)}%
                      </div>
                    </div>
                  )}
                  {typeof logSelecionado.threadCount === "number" && (
                    <div
                      style={{
                        backgroundColor: "#f1f2f6",
                        borderRadius: "6px",
                        padding: "8px 10px",
                      }}
                    >
                      <div style={{ fontSize: "10px", color: "#7f8c8d" }}>
                        THREADS ATIVAS
                      </div>
                      <div
                        style={{
                          fontSize: "15px",
                          fontWeight: "bold",
                          color: "#2c3e50",
                        }}
                      >
                        {logSelecionado.threadCount}
                      </div>
                    </div>
                  )}
                  {typeof logSelecionado.requisicoesSumulaneas === "number" && (
                    <div
                      style={{
                        backgroundColor: "#f1f2f6",
                        borderRadius: "6px",
                        padding: "8px 10px",
                      }}
                    >
                      <div style={{ fontSize: "10px", color: "#7f8c8d" }}>
                        REQUISIÇÕES SIMULTÂNEAS
                      </div>
                      <div
                        style={{
                          fontSize: "15px",
                          fontWeight: "bold",
                          color: "#2c3e50",
                        }}
                      >
                        {logSelecionado.requisicoesSumulaneas}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {logSelecionado.endpoint && (
              <div>
                <label
                  style={{
                    fontSize: "11px",
                    fontWeight: "bold",
                    color: "#999",
                    display: "block",
                    marginBottom: "2px",
                  }}
                >
                  ENDPOINT
                </label>
                <code
                  style={{
                    backgroundColor: "#f1f2f6",
                    padding: "4px 6px",
                    borderRadius: "4px",
                    fontSize: "13px",
                    display: "block",
                    wordBreak: "break-all",
                  }}
                >
                  {logSelecionado.endpoint}
                </code>
              </div>
            )}

            {logSelecionado.mensagem && (
              <div>
                <label
                  style={{
                    fontSize: "11px",
                    fontWeight: "bold",
                    color: "#999",
                    display: "block",
                    marginBottom: "2px",
                  }}
                >
                  MENSAGEM ORIGINAL
                </label>
                <p
                  style={{
                    margin: 0,
                    fontSize: "14px",
                    color: "#c5221f",
                    backgroundColor: "#fce8e6",
                    padding: "10px",
                    borderRadius: "6px",
                    lineHeight: "1.4",
                  }}
                >
                  {logSelecionado.mensagem}
                </p>
              </div>
            )}

            {logSelecionado.hermesInsight && (
              <div
                style={{
                  backgroundColor: "#ebf5fb",
                  borderLeft: "4px solid #2980b9",
                  padding: "14px",
                  borderRadius: "0 8px 8px 0",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    marginBottom: "6px",
                  }}
                >
                  <span style={{ fontSize: "16px" }}>🤖</span>
                  <strong style={{ fontSize: "13px", color: "#2980b9" }}>
                    HERMES INSIGHT (CO-PILOTO IA)
                  </strong>
                </div>
                <p
                  style={{
                    margin: 0,
                    fontSize: "13px",
                    color: "#2c3e50",
                    lineHeight: "1.4",
                    fontStyle: "italic",
                  }}
                >
                  "{logSelecionado.hermesInsight.sugestao}"
                </p>
              </div>
            )}

            {logSelecionado.stackTrace && (
              <div style={{ flex: 1 }}>
                <label
                  style={{
                    fontSize: "11px",
                    fontWeight: "bold",
                    color: "#999",
                    display: "block",
                    marginBottom: "4px",
                  }}
                >
                  STACK TRACE
                </label>
                <pre
                  style={{
                    margin: 0,
                    backgroundColor: "#2c3e50",
                    color: "#ecf0f1",
                    padding: "12px",
                    borderRadius: "6px",
                    fontSize: "11px",
                    overflowX: "auto",
                    whiteSpace: "pre-wrap",
                    maxHeight: "250px",
                    lineHeight: "1.4",
                  }}
                >
                  {logSelecionado.stackTrace}
                </pre>
              </div>
            )}
          </div>
          <div
            onClick={() => setLogSelecionado(null)}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "rgba(0,0,0,0.2)",
              zIndex: 999,
            }}
          />
        </>
      )}
    </div>
  );
};

export default PageAuditoria;
